<template>
  <section class="my-4 rounded-lg border p-4 space-y-3" aria-label="Module fees">
    <h3 class="font-semibold">Fees for the modules you picked</h3>
    <p class="text-sm text-gray-600">
      Tap "Get bill for these modules" when you're ready, then pay it on your Billing page. Each module is
      billed once per attempt. Failed a module and your school allows retakes? Use "Start retake" to get a new
      bill at the retake fee. Removed a module after getting its bill? Ask the bursary to cancel that bill.
    </p>
    <Message v-if="errorText" severity="error" :closable="false" data-testid="module-payment-error">{{ errorText }}</Message>
    <p v-if="loading">Checking your module fees…</p>
    <template v-if="summary">
      <DataTable :value="summary.modules" size="small">
        <Column header="Module">
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <span>{{ data.module_name }}</span>
              <Tag v-if="data.attempt_number > 1" :value="`Attempt ${data.attempt_number}`" severity="warn" />
            </div>
          </template>
        </Column>
        <Column header="Fee"><template #body="{ data }">{{ formatCurrency(data.amount) }}</template></Column>
        <Column header="Paid"><template #body="{ data }">{{ formatCurrency(data.paid) }}</template></Column>
        <Column header="Balance owed"><template #body="{ data }">{{ formatCurrency(data.outstanding) }}</template></Column>
      </DataTable>
      <p class="font-medium">Total: {{ formatCurrency(summary.amount) }} · Balance owed: {{ formatCurrency(summary.outstanding) }}</p>
      <Message v-if="summary.cleared" severity="success" :closable="false">All module fees are paid.</Message>
      <Message v-else severity="info" :closable="false">
        Pay each module's fee in full before you submit your registration. Your school may have other fees to pay too.
      </Message>
      <div class="flex gap-2 flex-wrap">
        <Button
          v-if="summary.modules.some(module => module.amount > 0 && !module.bill_id)"
          label="Get bill for these modules"
          :loading="preparing"
          data-testid="btn-create-module-bills"
          @click="createInvoices"
        />
        <Button v-if="canStartRetake" label="Start retake" severity="warn" outlined :loading="preparing" data-testid="btn-start-retake" @click="startRetake" />
        <Button
          v-if="summary.modules.some(module => module.bill_id && module.outstanding > 0)"
          label="Pay now"
          outlined
          data-testid="btn-module-pay-now"
          @click="router.push({ name: 'student-billing' })"
        />
        <Button label="Check again" text :loading="loading" data-testid="btn-module-check-again" @click="$emit('refresh')" />
      </div>
    </template>
  </section>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePrepareModulePayments } from '@/composables/mutations/usePrepareModulePayments';
import type { ModulePaymentSummary } from '@/types/composition-functions';
import { formatCurrency } from '@/utils/currency';
import { friendlyMessage } from '@/utils/error';
const props = defineProps<{ moduleIds: string[]; summary?: ModulePaymentSummary | null; loading?: boolean; error?: string }>();
const emit = defineEmits<{ refresh: [] }>();
const router = useRouter();
const { prepare, loading: preparing, error: prepareError } = usePrepareModulePayments();
// The query error arrives as raw text; never show server/technical wording to a student.
const errorText = computed(() => {
  if (props.error) return friendlyMessage(props.error, 'We couldn\'t check your module fees. Tap "Check again".');
  return prepareError.value;
});
// A retake can be initiated only when every currently displayed module invoice
// is fully paid. The backend enforces the same rule; disabling the button here
// gives a clearer UX.
const canStartRetake = computed(() => !!props.summary
  && props.summary.modules.length > 0
  && props.summary.modules.every(m => m.amount === 0 || (!!m.bill_id && m.outstanding === 0)));
async function createInvoices() {
  if (await prepare(props.moduleIds)) emit('refresh');
}
async function startRetake() {
  if (await prepare(props.moduleIds, true)) emit('refresh');
}
</script>
<style scoped>
:deep(.p-message-content) {
  height: auto;
  min-height: 0;
}
</style>
